'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { recortarMarcha } from '@/lib/actions';
import { cn } from '@/lib/utils';
import { Loader2, Pause, Play, Save, Scissors, SkipBack } from 'lucide-react';
import type { MarchaDoc } from '@/lib/types';

/** `m:ss` a partir de milisegundos. */
export function msATexto(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** `m:ss`, `mm:ss` o un número de segundos sueltos → milisegundos. `null` si no se entiende. */
export function textoAMs(txt: string): number | null {
  const t = (txt || '').trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t) * 1000;
  const m = t.match(/^(\d+)\s*:\s*(\d{1,2})$/);
  if (!m) return null;
  const seg = Number(m[2]);
  if (seg > 59) return null;
  return (Number(m[1]) * 60 + seg) * 1000;
}

/**
 * Reproductor GRANDE de una marcha con RECORTE: desde y hasta qué segundo suena
 * dentro del juego.
 *
 * <p><b>El audio no se corta.</b> El OGG se sigue sirviendo entero y direccionado por su
 * contenido; lo que se guarda son dos números que viajan en el manifiesto, y es el mod
 * el que entra por el principio elegido y termina en el final elegido. Cortar el fichero
 * cambiaría su hash y obligaría a todos los jugadores a volver a bajárselo cada vez que
 * se mueve el recorte un segundo.</p>
 *
 * <p>La barra enseña el trozo elegido resaltado y lo de fuera apagado, que es lo que hace
 * entender de un vistazo qué se va a oír. «Probar» reproduce SOLO ese trozo, exactamente
 * como sonará en el juego.</p>
 */
export function RecorteMarcha({
  marcha, onClose, onGuardado,
}: {
  marcha: MarchaDoc | null;
  onClose: () => void;
  onGuardado?: () => void;
}) {
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);

  const [dur, setDur] = useState(0);          // duración real del audio (ms)
  const [pos, setPos] = useState(0);          // dónde va la aguja (ms)
  const [sonando, setSonando] = useState(false);
  const [ini, setIni] = useState(0);
  const [fin, setFin] = useState(0);          // 0 = hasta el final
  const [iniTxt, setIniTxt] = useState('0:00');
  const [finTxt, setFinTxt] = useState('');
  const [soloRecorte, setSoloRecorte] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [arrastrando, setArrastrando] = useState<'ini' | 'fin' | 'aguja' | null>(null);

  // Al abrir con otra marcha se reinicia todo con SUS valores guardados.
  useEffect(() => {
    if (!marcha) return;
    const i = Number(marcha.recorteIniMs) || 0;
    const f = Number(marcha.recorteFinMs) || 0;
    setDur(Number(marcha.duracionMs) || 0);
    setPos(i);
    setIni(i);
    setFin(f);
    setIniTxt(msATexto(i));
    setFinTxt(f > 0 ? msATexto(f) : '');
    setSonando(false);
  }, [marcha?.id]);

  const finReal = fin > 0 ? fin : dur;
  const abierto = !!marcha;

  const buscar = useCallback((ms: number) => {
    const a = audioRef.current;
    const v = Math.max(0, Math.min(dur || ms, ms));
    setPos(v);
    if (a && Number.isFinite(a.duration)) a.currentTime = v / 1000;
  }, [dur]);

  // Vigila la aguja y, con «solo el recorte», corta al llegar al final elegido:
  // así se oye exactamente lo que sonará en el juego.
  useEffect(() => {
    if (!abierto) return;
    const id = window.setInterval(() => {
      const a = audioRef.current;
      if (!a) return;
      const ms = a.currentTime * 1000;
      setPos(ms);
      if (soloRecorte && finReal > 0 && ms >= finReal - 20) {
        a.pause();
        a.currentTime = ini / 1000;
        setSonando(false);
        setPos(ini);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [abierto, soloRecorte, finReal, ini]);

  const alternar = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (soloRecorte && (pos < ini || (finReal > 0 && pos >= finReal))) {
        a.currentTime = ini / 1000;
        setPos(ini);
      }
      a.play().then(() => setSonando(true)).catch(() => setSonando(false));
    } else {
      a.pause();
      setSonando(false);
    }
  };

  // ── Barra ──────────────────────────────────────────────────────────────────
  const msDeX = useCallback((clientX: number) => {
    const el = barraRef.current;
    if (!el || !dur) return 0;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return Math.round(f * dur);
  }, [dur]);

  const aplicar = useCallback((que: 'ini' | 'fin' | 'aguja', ms: number) => {
    if (que === 'aguja') { buscar(ms); return; }
    if (que === 'ini') {
      const v = Math.min(ms, (fin > 0 ? fin : dur) - 500);
      const nv = Math.max(0, v);
      setIni(nv);
      setIniTxt(msATexto(nv));
    } else {
      const v = Math.max(ms, ini + 500);
      const nv = v >= dur ? 0 : v;      // hasta el final
      setFin(nv);
      setFinTxt(nv > 0 ? msATexto(nv) : '');
    }
  }, [buscar, dur, fin, ini]);

  useEffect(() => {
    if (!arrastrando) return;
    const mover = (e: PointerEvent) => aplicar(arrastrando, msDeX(e.clientX));
    const soltar = () => setArrastrando(null);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
  }, [arrastrando, aplicar, msDeX]);

  const pct = (ms: number) => (dur > 0 ? Math.max(0, Math.min(100, (ms / dur) * 100)) : 0);

  const guardar = async () => {
    if (!marcha) return;
    setGuardando(true);
    const r = await recortarMarcha(marcha.id, ini, fin);
    setGuardando(false);
    toast({
      title: r.success ? 'Recorte guardado' : 'Error',
      description: r.message,
      variant: r.success ? undefined : 'destructive',
    });
    if (r.success) { onGuardado?.(); onClose(); }
  };

  const duracionUsada = useMemo(() => Math.max(0, finReal - ini), [finReal, ini]);

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) { audioRef.current?.pause(); onClose(); } }}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-4 w-4 text-primary" />
            {marcha?.nombre || 'Marcha'}
          </DialogTitle>
          <DialogDescription>
            Elige desde y hasta qué segundo suena <strong>dentro del juego</strong>. El audio no se
            corta: se guardan los dos puntos y el mod entra y sale por ahí.
          </DialogDescription>
        </DialogHeader>

        {/* Div plano con `min-h-0`, no ScrollArea (gotcha de los diálogos con formulario). */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 pb-6">
          <audio
            ref={audioRef}
            src={marcha?.audioUrl}
            preload="metadata"
            onLoadedMetadata={(e) => {
              const ms = Math.round(e.currentTarget.duration * 1000);
              if (Number.isFinite(ms) && ms > 0) setDur(ms);
            }}
            onEnded={() => setSonando(false)}
            className="hidden"
          />

          {/* ── LA BARRA ── */}
          <div className="select-none pt-6">
            <div
              ref={barraRef}
              onPointerDown={(e) => { setArrastrando('aguja'); aplicar('aguja', msDeX(e.clientX)); }}
              className="relative h-12 w-full cursor-pointer rounded-xl border border-white/10 bg-black/50"
            >
              {/* Fuera del recorte: apagado. Dentro: el color de la web. */}
              <div className="absolute inset-y-0 rounded-l-xl bg-white/[0.04]" style={{ left: 0, width: `${pct(ini)}%` }} />
              <div
                className="absolute inset-y-0 bg-primary/25"
                style={{ left: `${pct(ini)}%`, width: `${Math.max(0, pct(finReal) - pct(ini))}%` }}
              />
              <div
                className="absolute inset-y-0 rounded-r-xl bg-white/[0.04]"
                style={{ left: `${pct(finReal)}%`, right: 0 }}
              />

              {/* Aguja */}
              <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${pct(pos)}%` }} />

              {/* Tiradores del principio y del final */}
              {(['ini', 'fin'] as const).map((que) => {
                const ms = que === 'ini' ? ini : finReal;
                return (
                  <div
                    key={que}
                    onPointerDown={(e) => { e.stopPropagation(); setArrastrando(que); }}
                    title={que === 'ini' ? 'Empieza aquí' : 'Termina aquí'}
                    className={cn(
                      'absolute -top-1 h-14 w-3 -translate-x-1/2 cursor-ew-resize rounded-full border-2 border-black/60',
                      que === 'ini' ? 'bg-emerald-400' : 'bg-rose-400',
                    )}
                    style={{ left: `${pct(ms)}%` }}
                  />
                );
              })}
            </div>
            <div className="mt-1 flex justify-between text-[0.7rem] text-muted-foreground">
              <span>{msATexto(pos)}</span>
              <span>
                Suena <strong className="text-foreground">{msATexto(duracionUsada)}</strong> de {msATexto(dur)}
              </span>
            </div>
          </div>

          {/* ── Mandos ── */}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={alternar} className="gap-2" disabled={!dur}>
              {sonando ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {sonando ? 'Pausa' : 'Probar'}
            </Button>
            <Button variant="secondary" className="gap-1.5" onClick={() => buscar(ini)}>
              <SkipBack className="h-3.5 w-3.5" /> Al principio del recorte
            </Button>
            <Button variant="secondary" onClick={() => aplicar('ini', pos)}>Empieza aquí</Button>
            <Button variant="secondary" onClick={() => aplicar('fin', pos)}>Termina aquí</Button>
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={soloRecorte} onChange={(e) => setSoloRecorte(e.target.checked)} />
              Oír solo el trozo elegido
            </label>
          </div>

          {/* ── Los dos números, escribibles a mano ── */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Empieza en</Label>
              <Input
                value={iniTxt}
                onChange={(e) => setIniTxt(e.target.value)}
                onBlur={() => {
                  const ms = textoAMs(iniTxt);
                  if (ms == null) { setIniTxt(msATexto(ini)); return; }
                  aplicar('ini', ms);
                }}
                placeholder="0:00"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Termina en (vacío = hasta el final)</Label>
              <Input
                value={finTxt}
                onChange={(e) => setFinTxt(e.target.value)}
                onBlur={() => {
                  if (!finTxt.trim()) { setFin(0); return; }
                  const ms = textoAMs(finTxt);
                  if (ms == null) { setFinTxt(fin > 0 ? msATexto(fin) : ''); return; }
                  aplicar('fin', ms);
                }}
                placeholder={msATexto(dur)}
              />
            </div>
          </div>
          <p className="text-[0.7rem] text-muted-foreground">
            Vale <code>1:23</code> o los segundos a pelo (<code>83</code>). El cambio llega al servidor
            del mod en menos de 15 segundos: nadie tiene que reconectar.
          </p>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => { aplicar('ini', 0); setFin(0); setFinTxt(''); }}
            >
              Quitar el recorte
            </Button>
            <Button onClick={guardar} disabled={guardando} className="gap-2">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
